import { useEffect, useState } from 'react'
import { Link, NavLink as RouterNavLink } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Menu, X } from 'lucide-react'
import { primaryNav } from '../../data/nav'
import { useSiteData } from '../../lib/SiteDataProvider'
import { Button } from '../ui/Button'
import logo from '../../assets/polakis-logo.png'

export function Header() {
  const { products } = useSiteData()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openGroup, setOpenGroup] = useState<string | null>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 shadow-sm backdrop-blur-md' : 'bg-white/70 backdrop-blur-sm'
      } border-b border-ink-100`}
    >
      <div className="mx-auto flex h-16 max-w-8xl items-center justify-between px-6 lg:h-20 lg:px-10">
        <Link to="/" className="flex shrink-0 items-center" onClick={() => setOpenGroup(null)}>
          <img src={logo} alt="Polakis Technologies" className="h-9 w-auto lg:h-10" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpenGroup(null)}>
          {primaryNav.map((item) =>
            item.label === 'Products' ? (
              <div key={item.label} className="relative" onMouseEnter={() => setOpenGroup('Products')}>
                <button className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-50 hover:text-ink-900">
                  Products
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <AnimatePresence>
                  {openGroup === 'Products' && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-1/2 top-full w-[640px] -translate-x-1/2 pt-3"
                    >
                      <div className="grid grid-cols-2 gap-1 rounded-2xl border border-ink-100 bg-white p-4 shadow-card-hover">
                        {products.map((p) => (
                          <Link
                            key={p.slug}
                            to={`/products/${p.slug}`}
                            onClick={() => setOpenGroup(null)}
                            className="group flex flex-col rounded-xl px-4 py-3 transition-colors hover:bg-ink-50"
                          >
                            <span className="text-sm font-semibold text-ink-900 group-hover:text-teal-700">
                              {p.name}
                              {p.suite && <span className="text-ink-400"> {p.suite}</span>}
                            </span>
                            <span className="mt-0.5 text-xs text-ink-500">{p.category}</span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <RouterNavLink
                key={item.label}
                to={item.href}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors hover:bg-ink-50 hover:text-ink-900 ${
                    isActive ? 'text-ink-900' : 'text-ink-700'
                  }`
                }
              >
                {item.label}
              </RouterNavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/contact" className="text-sm font-medium text-ink-700 hover:text-ink-900">
            Contact
          </Link>
          <Button href="/contact?intent=demo" size="md">
            Book Demo
          </Button>
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-full text-ink-800 lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-ink-100 bg-white lg:hidden"
          >
            <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
              {primaryNav.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="block border-b border-ink-100 py-3 text-base font-medium text-ink-800"
                >
                  {item.label}
                </Link>
              ))}
              <div className="mt-2 pl-2">
                <p className="py-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Product Ecosystem</p>
                {products.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/products/${p.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 text-sm text-ink-600"
                  >
                    {p.name} {p.suite}
                  </Link>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-3">
                <Button href="/contact?intent=demo" className="w-full justify-center">
                  Book Demo
                </Button>
                <Button href="/contact" variant="ghost" className="w-full justify-center">
                  Contact Sales
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
